import React, { useEffect, useState } from 'react';
import { Table, Modal, Input, Select, message } from 'antd';
import { api, getAdminErrorMessage } from '../lib/api';
import { showBulkActionFeedback } from '../lib/bulkActionFeedback';
import BulkActionModal from '../components/BulkActionModal';
const SUPPORT_TICKETS_VIEW_KEY = 'admin_support_tickets_view';

const statusStyle = {
  OPEN: { bg: '#FFF3E0', color: '#C77700', text: '待处理' },
  PROCESSING: { bg: '#E0F2FE', color: '#0369A1', text: '处理中' },
  RESOLVED: { bg: '#EAFBF1', color: '#157347', text: '已解决' },
  CLOSED: { bg: '#F3F4F6', color: '#4B5563', text: '已关闭' },
};

const priorityStyle = {
  LOW: { bg: '#F3F4F6', color: '#4B5563', text: '低' },
  NORMAL: { bg: '#E8F1FF', color: '#2457D6', text: '普通' },
  HIGH: { bg: '#FFF3E0', color: '#C77700', text: '高' },
  URGENT: { bg: '#FFF1F2', color: '#BE123C', text: '紧急' }
};

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyStatus, setReplyStatus] = useState('PROCESSING');
  const [submitting, setSubmitting] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [reportOnly, setReportOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkStatusModal, setBulkStatusModal] = useState({ open: false, status: null });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SUPPORT_TICKETS_VIEW_KEY) || 'null');
      if (!saved) return;
      setKeyword(saved.keyword || '');
      setStatusFilter(saved.statusFilter || 'all');
      setPriorityFilter(saved.priorityFilter || 'all');
      setReportOnly(Boolean(saved.reportOnly));
      setPageSize(saved.pageSize || 10);
      setSortBy(saved.sortBy || 'updatedAt');
      setSortDirection(saved.sortDirection || 'desc');
    } catch (_) {
      localStorage.removeItem(SUPPORT_TICKETS_VIEW_KEY);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTickets();
    }, 250);
    return () => clearTimeout(timer);
  }, [keyword, statusFilter, priorityFilter, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    localStorage.setItem(SUPPORT_TICKETS_VIEW_KEY, JSON.stringify({
      keyword,
      statusFilter,
      priorityFilter,
      reportOnly,
      pageSize,
      sortBy,
      sortDirection
    }));
  }, [keyword, statusFilter, priorityFilter, reportOnly, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [keyword, statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/support/tickets', {
        params: {
          keyword: keyword.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          priority: priorityFilter === 'all' ? undefined : priorityFilter,
          page,
          pageSize,
          sortBy,
          sortDirection
        }
      });
      setTickets(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载工单失败'));
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (ticketId) => {
    try {
      const response = await api.get(`/api/admin/support/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      setReplyStatus(response.data.status === 'OPEN' ? 'PROCESSING' : response.data.status);
      setReplyContent('');
      setDetailVisible(true);
    } catch (error) {
      message.error(error.response?.data?.message || '加载详情失败');
    }
  };

  const submitReply = async () => {
    if (!replyContent.trim()) {
      message.warning('请输入回复内容');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/api/admin/support/tickets/${selectedTicket.id}/reply`, {
        content: replyContent,
        status: replyStatus,
      });
      setSelectedTicket(response.data);
      setReplyContent('');
      message.success('回复成功');
      fetchTickets();
    } catch (error) {
      message.error(error.response?.data?.message || '回复失败');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      const response = await api.post(`/api/admin/support/tickets/${ticketId}/status`, { status });
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(response.data);
      }
      message.success('状态已更新');
      fetchTickets();
    } catch (error) {
      message.error(error.response?.data?.message || '更新状态失败');
    }
  };

  const openBulkStatusModal = (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择工单');
      return;
    }
    setBulkStatusModal({ open: true, status });
  };

  const submitBulkStatus = async (ids) => {
    const normalizedIds = ids || selectedRowKeys;
    if (!bulkStatusModal.status || normalizedIds.length === 0) return;
    setBulkSubmitting(true);
    try {
      const response = await api.post('/api/admin/support/tickets/bulk-status', {
        ids: normalizedIds,
        status: bulkStatusModal.status
      });
      showBulkActionFeedback(response.data, {
        entityLabel: '工单',
        successVerb: '更新',
        onSelectFailed: response.data.failedIds?.length ? (failedIds) => setSelectedRowKeys(failedIds.map((id) => Number(id))) : undefined,
        onRetry: response.data.failedIds?.length ? submitBulkStatus : undefined
      });
      if (!response.data.failedIds?.length) {
        setSelectedRowKeys([]);
      }
      setBulkStatusModal({ open: false, status: null });
      fetchTickets();
    } catch (error) {
      message.error(error.response?.data?.message || '批量更新失败');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkStatus = async () => {
    await submitBulkStatus();
  };

  const resetFilters = () => {
    setKeyword('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setReportOnly(false);
    setSortBy('updatedAt');
    setSortDirection('desc');
    setPage(1);
    setPageSize(10);
    localStorage.removeItem(SUPPORT_TICKETS_VIEW_KEY);
  };

  const sortLabel = getTicketSortLabel(sortBy, sortDirection);
  const displayedTickets = reportOnly ? tickets.filter(isReportTicket) : tickets;

  const columns = [
    {
      title: '类型',
      key: 'type',
      width: 110,
      render: (_, record) => isReportTicket(record)
        ? <span style={pillStyle('#FFF1F2', '#BE123C')}>举报审核</span>
        : <span style={pillStyle('#E8F1FF', '#2457D6')}>客服咨询</span>,
    },
    {
      title: '工单ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      sorter: true,
      sortOrder: sortBy === 'id' ? toAntdSortOrder(sortDirection) : null,
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '标题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 110,
      sorter: true,
      sortOrder: sortBy === 'priority' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => {
        const style = priorityStyle[value] || priorityStyle.NORMAL;
        return <span style={pillStyle(style.bg, style.color)}>{style.text}</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: true,
      sortOrder: sortBy === 'status' ? toAntdSortOrder(sortDirection) : null,
      render: (value) => {
        const style = statusStyle[value] || statusStyle.OPEN;
        return <span style={pillStyle(style.bg, style.color)}>{style.text}</span>;
      },
    },
    {
      title: '最后回复',
      dataIndex: 'lastReplyAt',
      key: 'lastReplyAt',
      width: 200,
      sorter: true,
      sortOrder: sortBy === 'lastReplyAt' ? toAntdSortOrder(sortDirection) : null,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => openDetail(record.id)}
            style={buttonStyle('primary')}
          >
            查看
          </button>
          <button
            onClick={() => updateStatus(record.id, 'PROCESSING')}
            style={buttonStyle('primary')}
          >
            处理中
          </button>
          <button
            onClick={() => updateStatus(record.id, 'RESOLVED')}
            style={buttonStyle('success')}
          >
            设为已解决
          </button>
          <button
            onClick={() => updateStatus(record.id, 'CLOSED')}
            style={buttonStyle('neutral')}
          >
            关闭
          </button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px', background: 'transparent', minHeight: '100%' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2a44', marginBottom: '6px' }}>
          客服与举报审核
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          举报单会保留对象、原因、内容摘要和审核回复；处理结果会同步给提交举报的用户。
        </div>
      </div>

      <div style={toolbarStyle}>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索工单标题、用户、ID"
          allowClear
          style={filterInputStyle}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部状态</option>
          <option value="OPEN">待处理</option>
          <option value="PROCESSING">处理中</option>
          <option value="RESOLVED">已解决</option>
          <option value="CLOSED">已关闭</option>
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部优先级</option>
          <option value="LOW">低</option>
          <option value="NORMAL">普通</option>
          <option value="HIGH">高</option>
          <option value="URGENT">紧急</option>
        </select>
        <button onClick={() => setReportOnly(!reportOnly)} style={buttonStyle(reportOnly ? 'danger' : 'neutral')}>
          {reportOnly ? '仅看举报：开' : '仅看举报：关'}
        </button>
        <button onClick={() => openBulkStatusModal('PROCESSING')} style={buttonStyle('primary')}>批量处理中</button>
        <button onClick={() => openBulkStatusModal('RESOLVED')} style={buttonStyle('success')}>批量已解决</button>
        <button onClick={() => openBulkStatusModal('CLOSED')} style={buttonStyle('neutral')}>批量关闭</button>
        <button onClick={resetFilters} style={buttonStyle('neutral')}>恢复默认</button>
        <div style={toolbarHintStyle}>
          {selectedRowKeys.length > 0 ? `已选 ${selectedRowKeys.length} 个 · ` : ''}
          当前排序：{sortLabel} · 显示 {displayedTickets.length} / {total} 条
        </div>
      </div>

      <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)', borderRadius: '18px', boxShadow: '0 20px 45px rgba(31, 42, 68, 0.08)', overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={displayedTickets}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          onChange={(pagination, _, sorter) => {
            const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            setPage(pagination.current || 1);
            setPageSize(pagination.pageSize || 10);
            if (nextSorter?.field) {
              setSortBy(String(nextSorter.field));
              setSortDirection(nextSorter.order === 'ascend' ? 'asc' : 'desc');
            } else {
              setSortBy('updatedAt');
              setSortDirection('desc');
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }
          }}
          scroll={{ x: 980 }}
        />
      </div>

      <Modal
        title={selectedTicket ? `${isReportTicket(selectedTicket) ? '举报审核' : '工单详情'} #${selectedTicket.id} - ${selectedTicket.subject}` : '工单详情'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={760}
      >
        {selectedTicket && (
          <div>
            <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span style={pillStyle((statusStyle[selectedTicket.status] || statusStyle.OPEN).bg, (statusStyle[selectedTicket.status] || statusStyle.OPEN).color)}>
                {(statusStyle[selectedTicket.status] || statusStyle.OPEN).text}
              </span>
              <span style={pillStyle((priorityStyle[selectedTicket.priority] || priorityStyle.NORMAL).bg, (priorityStyle[selectedTicket.priority] || priorityStyle.NORMAL).color)}>
                {(priorityStyle[selectedTicket.priority] || priorityStyle.NORMAL).text}
              </span>
              <span>用户：{selectedTicket.username}</span>
              <span>创建时间：{selectedTicket.createdAt}</span>
              <span>最后回复：{selectedTicket.lastReplyAt}</span>
              {isReportTicket(selectedTicket) && <span style={{ color: '#BE123C', fontWeight: 600 }}>请核对举报对象、原因和内容摘要后再处理</span>}
            </div>

            <div style={{ maxHeight: '360px', overflowY: 'auto', background: '#f8fbff', padding: '14px', borderRadius: '16px', marginBottom: '16px', border: '1px solid #e6eefc' }}>
              {selectedTicket.messages?.map((item) => (
                <div
                  key={item.id}
                  style={{
                    marginBottom: '12px',
                    padding: '14px',
                    borderRadius: '14px',
                    background: item.senderType === 'STAFF' ? '#e8f1ff' : '#ffffff',
                    border: '1px solid #e6eefc',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '6px' }}>
                    {item.senderName} · {item.senderType} · {item.createdAt}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', color: '#262626' }}>{item.content}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <Select
                value={replyStatus}
                onChange={setReplyStatus}
                options={[
                  { value: 'PROCESSING', label: '处理中' },
                  { value: 'RESOLVED', label: '已解决' },
                  { value: 'CLOSED', label: '已关闭' },
                ]}
                style={{ width: 160 }}
              />
            </div>

            <Input.TextArea
              rows={4}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={isReportTicket(selectedTicket) ? "输入审核结论、处置措施和回复内容" : "输入处理结果或回复内容"}
              style={replyAreaStyle}
            />

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setDetailVisible(false)} style={buttonStyle('neutral')}>
                关闭
              </button>
              <button onClick={submitReply} disabled={submitting} style={buttonStyle('primary', submitting)}>
                {submitting ? '提交中...' : isReportTicket(selectedTicket) ? '提交审核结论' : '发送回复'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <BulkActionModal
        open={bulkStatusModal.open}
        title="批量更新工单状态"
        confirmText="确认批量更新"
        confirmVariant={bulkStatusModal.status === 'RESOLVED' ? 'success' : 'primary'}
        confirmLoading={bulkSubmitting}
        onCancel={() => setBulkStatusModal({ open: false, status: null })}
        onConfirm={handleBulkStatus}
        summaryLines={[
          `选中工单：${selectedRowKeys.length} 个`,
          `目标状态：${getTicketStatusLabel(bulkStatusModal.status)}`
        ]}
        note="批量状态流转适合处理同类工单；如果其中有需要单独沟通的工单，建议先取消勾选。"
      />
    </div>
  );
};

const pillStyle = (backgroundColor, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  backgroundColor,
  color,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '999px'
});

const replyAreaStyle = {
  border: '1px solid #d6e4ff',
  borderRadius: '14px',
  padding: '10px 12px',
  backgroundColor: '#f8fbff'
};

const toolbarStyle = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px 18px',
  marginBottom: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.82)',
  borderRadius: '16px',
  boxShadow: '0 14px 30px rgba(31, 42, 68, 0.06)'
};

const filterInputStyle = {
  flex: '1 1 280px',
  minWidth: '220px'
};

const filterSelectStyle = {
  minWidth: '160px',
  height: '40px',
  padding: '0 12px',
  borderRadius: '12px',
  border: '1px solid #d6e4ff',
  backgroundColor: '#f8fbff',
  color: '#1f2a44'
};

const toolbarHintStyle = {
  marginLeft: 'auto',
  color: '#6b7280',
  fontSize: '13px',
  fontWeight: 600
};

const toAntdSortOrder = (direction) => direction === 'asc' ? 'ascend' : 'descend';

const getTicketSortLabel = (sortBy, sortDirection) => {
  const labels = {
    updatedAt: '更新时间',
    lastReplyAt: '最后回复',
    id: '工单ID',
    priority: '优先级',
    status: '状态'
  };
  return `${labels[sortBy] || '更新时间'}${sortDirection === 'asc' ? '升序' : '降序'}`;
};

const getTicketStatusLabel = (status) => {
  const labels = {
    OPEN: '待处理',
    PROCESSING: '处理中',
    RESOLVED: '已解决',
    CLOSED: '已关闭'
  };
  return labels[status] || status || '-';
};

const isReportTicket = (ticket) => /举报/.test(ticket?.subject || '') || /举报来源：/.test(ticket?.messages?.[0]?.content || '');

const buttonStyle = (variant = 'neutral', disabled = false) => {
  const variants = {
    neutral: {
      background: '#f3f6fb',
      color: '#1f2a44',
      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.22)'
    },
    primary: {
      background: 'linear-gradient(135deg, #1677ff 0%, #2457d6 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(22, 119, 255, 0.22)'
    },
    success: {
      background: 'linear-gradient(135deg, #34c759 0%, #159947 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(21, 153, 71, 0.2)'
    },
    danger: {
      background: '#BE123C',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(190, 18, 60, 0.18)'
    }
  };
  const style = variants[variant] || variants.neutral;
  return {
    padding: '7px 14px',
    background: style.background,
    color: style.color,
    border: 'none',
    borderRadius: '999px',
    boxShadow: disabled ? 'none' : style.boxShadow,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    opacity: disabled ? 0.7 : 1
  };
};

export default SupportTickets;
